// WebSocket transport. Opens to /api/{boardId}/ws, sends `hello`, reads
// server messages, dispatches to state. Reconnects with exponential
// backoff + jitter; queues outgoing ops while disconnected and flushes
// them on reopen.
//
// Resilience to silent disconnects (mobile network handoff, VPN flap,
// backgrounded mobile tab) is non-trivial: in those cases the browser
// often keeps `readyState === OPEN` indefinitely even though no bytes
// flow. We mitigate without a server-side heartbeat by listening to
// `window` `online` / `document` `visibilitychange` and force-closing
// the socket when we have reason to believe it's stale — the normal
// onclose → reconnect path then takes over.

import type { ClientMsg } from "./generated/ClientMsg";
import type { CursorPos } from "./generated/CursorPos";
import type { Op } from "./generated/Op";
import type { ServerMsg } from "./generated/ServerMsg";
import { wsUrl } from "./config/backend";

const BACKOFFS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const JITTER = 0.2;
const FAILED_ATTEMPTS_BEFORE_OFFLINE = 5;
// Any inbound silence longer than this on a "connected" socket is
// suspicious enough to force-close when the tab regains focus or the
// network comes back. Chosen comfortably above typical replay latency
// and roster churn on a quiet board.
const STALE_INBOUND_MS = 30_000;

export interface Transport {
  /** Send an op via the WS, or queue it if we're not connected. */
  sendOp(op: Op, clientOpId: string, baseSeq: number): void;
  sendCursor(pos: CursorPos | null): void;
  sendEditing(taskId: string | null): void;
  sendIdentify(name: string): void;
  close(): void;
}

export interface TransportHandlers {
  onConnected(): void;
  onReconnecting(): void;
  onOffline(): void;
  onServerMessage(msg: ServerMsg): void;
  /** Snapshot of `lastSeenSeq` for the next `hello`. */
  lastSeq(): number;
  /** Queued ops the transport should re-send on every (re)connect. */
  pendingOps(): Array<{ op: Op; clientOpId: string; baseSeq: number }>;
}

export function createTransport(
  boardId: string,
  handlers: TransportHandlers,
): Transport {
  let ws: WebSocket | null = null;
  let attempts = 0;
  let closing = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Timestamp of the last inbound frame. Used to detect zombie sockets
   *  whose `readyState` is OPEN but where the underlying connection has
   *  silently died (mobile network handoff, VPN flap, ISP NAT rebind). */
  let lastInboundMs = 0;
  /** Ops queued while the socket isn't OPEN. Flushed on every reconnect. */
  const queue: ClientMsg[] = [];

  const url = wsUrl(boardId);

  function connect(): void {
    if (closing) return;
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    handlers.onReconnecting();
    ws = new WebSocket(url);
    ws.onopen = () => {
      attempts = 0;
      lastInboundMs = Date.now();
      handlers.onConnected();
      // Replay flow: send hello with our last seen seq, then re-send any
      // ops the user queued during the gap.
      send({ kind: "hello", since: handlers.lastSeq() });
      for (const p of handlers.pendingOps()) {
        send({
          kind: "op",
          op: p.op,
          clientOpId: p.clientOpId,
          baseSeq: p.baseSeq,
        });
      }
      flushQueue();
    };
    ws.onmessage = (e) => {
      lastInboundMs = Date.now();
      if (typeof e.data !== "string") return;
      try {
        const msg = JSON.parse(e.data) as ServerMsg;
        handlers.onServerMessage(msg);
      } catch {
        // ignore malformed frames
      }
    };
    ws.onclose = () => {
      ws = null;
      if (closing) return;
      attempts++;
      if (attempts > FAILED_ATTEMPTS_BEFORE_OFFLINE) handlers.onOffline();
      else handlers.onReconnecting();
      scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose will fire next; nothing to do here.
    };
  }

  function scheduleReconnect(): void {
    const idx = Math.min(attempts - 1, BACKOFFS_MS.length - 1);
    const base = BACKOFFS_MS[Math.max(0, idx)]!;
    const jitter = base * JITTER * (Math.random() * 2 - 1);
    const delay = Math.max(250, base + jitter);
    reconnectTimer = setTimeout(connect, delay);
  }

  function send(msg: ClientMsg): void {
    if (ws !== null && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    } else {
      queue.push(msg);
    }
  }

  function flushQueue(): void {
    if (ws === null || ws.readyState !== WebSocket.OPEN) return;
    while (queue.length > 0) {
      ws.send(JSON.stringify(queue.shift()!));
    }
  }

  /** Kick a reconnect right now, bypassing the backoff timer. Use when
   *  we have positive evidence the underlying network just changed
   *  (online event, tab regained focus). Safe to call when already
   *  open: it's a no-op then. */
  function reconnectNow(): void {
    if (closing) return;
    if (ws !== null && ws.readyState === WebSocket.OPEN) return;
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    connect();
  }

  /** Force-close the socket if it claims OPEN but hasn't received a
   *  byte in a while. The browser sometimes keeps `readyState === OPEN`
   *  long after a mobile network handoff / VPN flap silently severs
   *  the underlying TCP connection; without this kick, `onclose` would
   *  never fire and we'd happily queue edits into the void. */
  function recheckLiveness(): void {
    if (closing || ws === null) return;
    if (ws.readyState !== WebSocket.OPEN) return;
    if (Date.now() - lastInboundMs < STALE_INBOUND_MS) return;
    // Drop the zombie; onclose handles reconnect.
    ws.close();
  }

  function onWindowOnline(): void {
    reconnectNow();
  }
  function onVisibilityChange(): void {
    if (document.visibilityState !== "visible") return;
    if (ws === null || ws.readyState !== WebSocket.OPEN) {
      reconnectNow();
    } else {
      recheckLiveness();
    }
  }

  window.addEventListener("online", onWindowOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);

  connect();

  return {
    sendOp(op, clientOpId, baseSeq) {
      send({ kind: "op", op, clientOpId, baseSeq });
    },
    sendCursor(pos) {
      send({ kind: "cursor", pos });
    },
    sendEditing(taskId) {
      send({ kind: "editing", taskId });
    },
    sendIdentify(name) {
      send({ kind: "identify", name });
    },
    close() {
      closing = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      window.removeEventListener("online", onWindowOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (ws !== null && ws.readyState !== WebSocket.CLOSED) ws.close();
    },
  };
}
